import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {LoadingButton} from '@mui/lab';
import {Box, Divider, IconButton} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {ExportField} from '../../../interfaces/Export';
import {saveFile} from '../../../utils/file';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import ExportFieldForm from '../../common/ExportFieldForm';
import ExportPdfButton from '../../common/ExportPdfButton';
import ModalFixed from '../../common/ModalFixed';
import SnackbarMessages from '../../common/SnackbarMessages';
import {AmsEmoduleInstallationHistoryTabConfig} from '.';

interface Props {
  opened: boolean;
  value?: AmsEmoduleInstallationHistoryTabConfig;
  onClose: VoidFunction;
  onSubmit: (fields: ExportField[]) => void;
}

export const AmsEmoduleInstallationHistoryExportModal = (props: Props) => {
  const {enqueueSnackbar} = useSnackbar();

  const [isFetching, setIsFetching] = useState(false);
  const [isExcelModalOpened, setIsExcelModalOpened] = useState(false);

  const fetchData = async (fields: string[]) => {
    setIsExcelModalOpened(false);
    setIsFetching(true);
    try {
      const params = {
        ...props.value?.params,
        fields,
        export: 'excel',
      };
      const resp = await API.get(
        `${apiBaseUrl}/ams/emodule/${props.value?.id}/installation-history`,
        {params, responseType: 'blob'}
      );
      const filename =
        resp?.headers['content-disposition']?.split('filename=')?.[1];

      saveFile(resp.data, filename);
      handleSubmit(fields);
    } catch (error: unknown) {
      const messages = getMessagesFromApiError(error, 'STRA_923f85df');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setIsFetching(false);
  };

  const handleSubmit = (fields: string[]) => {
    const exportFields = (props.value?.exportFields ?? []).map((f) => ({
      ...f,
      hidden: !fields.includes(f.field),
    }));
    props.onSubmit(exportFields);
  };

  return (
    <>
      <ModalFixed open={props.opened} onClose={props.onClose}>
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          gap={3}
          p={3}
        >
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" alignItems="center" width="100%" gap={1.5}>
              <FileDownloadIcon sx={{color: 'primary.main'}} />
              <Box fontSize={24}>Export Installation History</Box>
            </Box>
            <IconButton onClick={props.onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            gap={3}
          >
            <Divider>Export</Divider>
            <LoadingButton
              fullWidth
              size="large"
              variant="contained"
              loading={isFetching}
              onClick={() => setIsExcelModalOpened(true)}
            >
              Export to Excel
            </LoadingButton>
            <ExportPdfButton
              endpoint={`/ams/emodule/${props.value?.id}/installation-history`}
              defaultFilename="installation_history.pdf"
              params={props.value?.params as Record<string, unknown> | undefined}
              exportFields={props.value?.exportFields}
            />
          </Box>
        </Box>
      </ModalFixed>

      <ModalFixed
        open={isExcelModalOpened}
        onClose={() => setIsExcelModalOpened(false)}
      >
        <Box
          display="flex"
          flexDirection="column"
          position="relative"
          gap={3}
          p={3}
        >
          <ExportFieldForm
            height={150}
            exportFields={props.value?.exportFields}
            loading={isFetching}
            onChange={fetchData}
            close={() => setIsExcelModalOpened(false)}
          />
        </Box>
      </ModalFixed>
    </>
  );
};
